import React from 'react';
import styled, { keyframes } from 'styled-components';

const Hero = () => {
  return (
    <HeroSection>
      <ContentContainer>
        <Title>YaYa Digital</Title>
        <Subtitle>יוצרים חוויות דיגיטליות מתקדמות</Subtitle>
        <TagLine>פיתוח אתרים • אפליקציות • עיצוב UI/UX</TagLine>
        <ButtonsContainer>
          <CallButton href="tel:+972532856627" className="neon-button">
            <CallIcon>📞</CallIcon>
            התקשר עכשיו
          </CallButton>
        </ButtonsContainer>
      </ContentContainer>
      
      <FloatingElements>
        <PhoneFrame className="glass-effect">
          <PhoneContent>
            <PhoneHeader />
            <PhoneBlock />
            <PhoneBlock />
            <PhoneBlock />
          </PhoneContent>
        </PhoneFrame>
      </FloatingElements>
    </HeroSection>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const moveUp = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.2;
  }
  50% {
    opacity: 0.4;
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  position: relative;
  overflow: hidden;

  @media (max-width: 1024px) {
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 100px;
  }
`;

const ContentContainer = styled.div`
  text-align: center;
  max-width: 800px;
  z-index: 2;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 4.5rem;
  color: white;
  margin-bottom: 20px;
  text-shadow: 0 0 10px rgba(38, 208, 206, 0.5);
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.8rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const TagLine = styled.div`
  font-size: 1.2rem;
  color: rgba(38, 208, 206, 0.8);
  margin-top: 20px;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  right: 10%;
  top: 20%;
  transform-origin: center center;
  will-change: transform, opacity;
  animation: ${fadeIn} 1s ease-out forwards, ${moveUp} 3s ease-in-out infinite alternate;
  
  @media (max-width: 1024px) {
    position: relative;
    right: auto;
    top: auto;
    margin-top: 40px;
    transform: scale(0.8);
  }

  @media (max-width: 768px) {
    transform: scale(0.7);
    margin-top: 20px;
  }
`;

const PhoneFrame = styled.div`
  width: 280px;
  height: 560px;
  border-radius: 40px;
  position: relative;
  transform: rotate(-15deg);
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(38, 208, 206, 0.2);
  box-shadow: 
    0 0 30px rgba(38, 208, 206, 0.2),
    inset 0 0 20px rgba(38, 208, 206, 0.1);

  @media (max-width: 1024px) {
    transform: rotate(0);
    margin: 0 auto;
  }
`;

const PhoneContent = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const PhoneHeader = styled.div`
  height: 20px;
  width: 40%;
  background: rgba(38, 208, 206, 0.3);
  border-radius: 10px;
`;

const PhoneBlock = styled.div`
  height: 80px;
  width: 100%;
  background: rgba(38, 208, 206, 0.1);
  border-radius: 15px;
  animation: ${pulse} 2s infinite ease-in-out;
`;

const ButtonsContainer = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const CallButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-decoration: none;
  color: #fff;
  padding: 15px 30px;
  font-size: 1.1rem;
  position: relative;
  border-radius: 30px;
  background: rgba(38, 208, 206, 0.1);
  border: 2px solid rgba(38, 208, 206, 0.6);
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 
    0 0 15px rgba(38, 208, 206, 0.3),
    0 0 30px rgba(38, 208, 206, 0.1),
    inset 0 0 10px rgba(38, 208, 206, 0.2);
  text-shadow: 0 0 5px rgba(38, 208, 206, 0.5);

  &:hover {
    transform: translateY(-2px) scale(1.02);
    border-color: rgba(38, 208, 206, 0.8);
    box-shadow: 
      0 0 20px rgba(38, 208, 206, 0.4),
      0 0 40px rgba(38, 208, 206, 0.2),
      inset 0 0 15px rgba(38, 208, 206, 0.3);
    text-shadow: 0 0 8px rgba(38, 208, 206, 0.8);
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 12px 25px;
  }
`;

const CallIcon = styled.span`
  font-size: 1.2em;
  margin-left: 5px;
  filter: drop-shadow(0 0 5px rgba(38, 208, 206, 0.5));
  transition: transform 0.3s ease;
  will-change: transform;

  &:hover {
    transform: scale(1.1);
  }
`;

export default Hero; 